import { memo, useCallback, useMemo, useState } from "react";
import { getModifierGroupDescription } from "../../helpers";
import { CartItemModifierGroupModel, CartItemModifierModel, StoreModifierGroupModel, StoreModifierModel } from "../../models";
import clsx from "clsx";
import ModifierPrice from "./ModifierPrice";

type Props = {
  index: number,
  inflationContext: {
    typeId: number | string, 
    hasAdInflation: boolean,
    productPrice: number,
    productBasePrice: number,
  },
  errors?: {
    [g_id: string]: {
      g_name: string;
      message: string;
    };
  },
  modifierGroup: StoreModifierGroupModel,
  selectedModifiers: CartItemModifierGroupModel[],
  handleSelectModifier: (
    modGroup: StoreModifierGroupModel, 
    modifier: CartItemModifierModel
  ) => void
}

const TabModifierGroupComponent: React.FC<Props> = ({index: groupIndex, inflationContext, errors = {}, modifierGroup, selectedModifiers, handleSelectModifier}) => {
  
  const { g_id: groupId, g_name, modifier: modifiers } = modifierGroup;
  
  const hasError = Boolean(errors[groupId]);
  
  return (
    <>
      <div className="size_pizza">
        <h3 className={hasError ? "text-danger" : ""}>{g_name}</h3>
        <p className={hasError ? "text-danger" : ""}>
          <span>{getModifierGroupDescription(modifierGroup)}</span>
        </p>
        <ul className="row">
          {modifiers.map((modifier, modifierIndex) => (
            <ModifierComponent 
              key={modifierIndex}
              modifier={modifier}
              modifierGroup={modifierGroup}
              groupIndex={groupIndex}
              selectedModifiers={selectedModifiers}
              handleSelectModifier={handleSelectModifier}
              inflationContext={inflationContext} />
          ))}
        </ul>
      </div>
    </>
  )
}

type ModifierComponentProps = {
  groupIndex: number,
  modifier: StoreModifierModel,
  modifierGroup: StoreModifierGroupModel,
  selectedModifiers: CartItemModifierGroupModel[],
  handleSelectModifier: (modGroup: StoreModifierGroupModel, modifier: CartItemModifierModel) => void
  inflationContext: {
    typeId: number | string, 
    hasAdInflation: boolean,
    productPrice: number,
    productBasePrice: number,
  },
}

const ModifierComponent: React.FC<ModifierComponentProps> = ({modifier, modifierGroup, groupIndex, selectedModifiers, handleSelectModifier, inflationContext}) => {
  const { g_id: groupId } = modifierGroup;
  const { m_id: modifierId, m_name: modifierName } = modifier;

  const initCartModifier: CartItemModifierModel =
    selectedModifiers
      .find((g) => g.g_id === groupId)
      ?.modifiers.find((m) => m.m_id === modifierId) ?? {
        m_id: modifierId,
        m_name: modifierName,
        m_price: modifier.m_price,
        m_qty: 1,
        prices: [],
        selected: false
      };

  const [cartModifier, setCartModifier] = useState<CartItemModifierModel>(initCartModifier);

  // Memoize modifier selection logic
  const isSelected = useMemo(
    () =>
      selectedModifiers.some(
        (group) =>
          group.g_id === groupId &&
          group.modifiers.some((m) => m.m_id === modifierId)
      ),
    [selectedModifiers, groupId, modifierId]
  );

  const handleChange = useCallback(() => {
    const updatedCartModifier = {
      ...cartModifier,
      // prices: [...(prev.prices || []), attribute],
      selected: !isSelected,
    };

    setCartModifier(updatedCartModifier);
    handleSelectModifier(modifierGroup, updatedCartModifier);
  }, []);
  
  return (
    <li key={modifierId} className="col-4 col-md-4 mb-3">
      <div 
        className={clsx('d-flex align-items-center', isSelected && 'active')}
        onClick={handleChange}>
        {modifier.m_image && (
          <img width={36} height={36} className="me-2" style={{filter: "grayscale(1)"}} src={modifier.m_image} alt="" />
        )}
        <h6 className="text-center">
          {modifier.m_name} {" "}
          <span className={clsx(Number(modifier.m_price) == 0 && 'invisible')}>
            <ModifierPrice 
              inflationContext={inflationContext}
              groupIndex={groupIndex}
              modifier={modifier}
              cartModifier={cartModifier} />
          </span>
        </h6>
      </div>
    </li>
  );
}

export const TabModifierGroup = memo(TabModifierGroupComponent);